// pfs-textarea-field.ts — self-contained Textarea-Control (PFS-619).
//
// Wie <pfs-text-field>, nur mit <textarea> in der EIGENEN Shadow.
//
// API: data-label · data-name · data-placeholder · data-required · data-error ·
//   data-hint · data-value (INITIAL) · data-rows (default 3) · data-readonly.
//   Property el.value (get/set). Re-emittiert input+change composed.
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { installFieldValue, bindFieldControl } from "../../composition/field-value.js";
import { CONTROL_CSS } from "../../styles/control.js";

const STYLES = `
:host { display: flex; flex-direction: column; gap: var(--pfs-space-label, 0.5rem); }
.field { display: flex; flex-direction: column; gap: var(--pfs-space-label, 0.5rem); }
.lbl { display: block; font-size: 0.875rem; font-weight: 500; color: var(--pfs-color-on-surface); }
.lbl:empty { display: none; }
.req { color: var(--pfs-color-danger); }
${CONTROL_CSS}
textarea { resize: vertical; }
:host([data-error]:not([data-error=""])) textarea { border-color: var(--pfs-color-danger); }
.ro {
  padding: 0.5rem 0.6rem; box-sizing: border-box; white-space: pre-wrap;
  background: var(--pfs-color-surface-sunken);
  border: 1px solid var(--pfs-color-outline);
  border-radius: var(--pfs-radius-sm, 0.375rem);
  color: var(--pfs-color-on-surface-muted); font-size: 0.9rem;
}
.msg { font-size: 0.8rem; }
.msg:empty { display: none; }
.msg.error { color: var(--pfs-color-danger); }
.msg.hint { color: var(--pfs-color-on-surface-muted); }
`;

defineComponent({
  tagName: "pfs-textarea-field",
  mixins: [withShadow({ mode: "open", delegatesFocus: true })],
  // .value ab Konstruktion les-/schreibbar (pre-connect-sicher, PFS-645).
  created: installFieldValue,
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const label = document.createElement("label");
    label.className = "field";
    const lblEl = document.createElement("span");
    lblEl.className = "lbl";
    const area = document.createElement("textarea");
    const roEl = document.createElement("div");
    roEl.className = "ro";
    label.append(lblEl, area, roEl);
    root.appendChild(label);
    const msg = document.createElement("p");
    msg.className = "msg";
    root.appendChild(msg);

    // el.value an die Textarea binden (pre-connect gepuffert / data-value).
    bindFieldControl(host, area);

    const reemit = (type: string) => (): void => {
      host.dispatchEvent(new Event(type, { bubbles: true, composed: true }));
    };
    area.addEventListener("input", reemit("input"));
    area.addEventListener("change", reemit("change"));

    const paint = (): void => {
      const labelText = host.dataset.label ?? "";
      lblEl.textContent = labelText;
      if (host.dataset.required != null && labelText) {
        const s = document.createElement("span");
        s.className = "req";
        s.textContent = "*";
        lblEl.appendChild(s);
      }
      if (host.dataset.name != null) area.name = host.dataset.name;
      area.placeholder = host.dataset.placeholder ?? "";
      const rows = host.dataset.rows != null ? Number(host.dataset.rows) : 3;
      area.rows = rows;
      const ro = host.dataset.readonly != null;
      area.style.display = ro ? "none" : "";
      area.disabled = ro;
      roEl.style.display = ro ? "" : "none";
      if (ro) {
        roEl.textContent = host.dataset.value ?? area.value;
        // Mehrzeilen-Optik auch read-only: Box an data-rows orientiert (statt auf
        // Inhaltshöhe zu kollabieren = Einzeiler-Look).
        roEl.style.minHeight = `${(rows * 1.4).toFixed(1)}em`;
      }

      const err = (host.dataset.error ?? "").trim();
      const hint = (host.dataset.hint ?? "").trim();
      if (err) {
        msg.textContent = err;
        msg.className = "msg error";
      } else if (hint) {
        msg.textContent = hint;
        msg.className = "msg hint";
      } else {
        msg.textContent = "";
        msg.className = "msg";
      }
    };
    paint();

    const obs = new MutationObserver(paint);
    obs.observe(host, {
      attributes: true,
      attributeFilter: [
        "data-label",
        "data-required",
        "data-error",
        "data-hint",
        "data-readonly",
        "data-name",
        "data-placeholder",
        "data-rows",
      ],
    });
    ctx.cleanup.push(() => obs.disconnect());
  },
});
